// ============================================
// ModalCrearHijo.js
// ============================================
// Crea un expediente vinculado (anexo / garantia / servicio_postventa)
// heredando datos del padre. Cualquier usuario logueado puede crearlo.

import React, { useEffect, useState } from 'react';
import {
  API_BASE_CLI, FF, COLOR_OSCURO, COLOR_GRIS, COLOR_GRIS_CL, COLOR_BORDE,
  COLOR_PELIGRO, inputStyle, botonSecundario, botonPrimario,
  authHeaders, ShellModal, ToastFlotante, TIPO_EXPEDIENTE_VISUAL,
} from './helpersExpedientes';

const TIPOS_HIJO = [
  { v: 'anexo',              label: 'Anexo',     descripcion: 'Trabajo extra durante una obra activa' },
  { v: 'garantia',           label: 'Garantia',  descripcion: 'Reclamo bajo garantia de un servicio cerrado' },
  { v: 'servicio_postventa', label: 'Postventa', descripcion: 'Nueva venta a un cliente con expediente archivado' },
];

export default function ModalCrearHijo({ expedientePadre, onCerrar, onCreado, esCelular }) {
  const [necesidades, setNecesidades] = useState([]);
  const [titulo, setTitulo] = useState('');
  const [tipo, setTipo] = useState('');
  const [necesidad, setNecesidad] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let activo = true;
    fetch(`${API_BASE_CLI}/api/catalogos/tipos-necesidad`)
      .then(r => r.json())
      .then(j => { if (activo) setNecesidades(Array.isArray(j) ? j : []); })
      .catch(() => {});
    return () => { activo = false; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !enviando) onCerrar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enviando, onCerrar]);

  const tipoMeta = tipo ? TIPO_EXPEDIENTE_VISUAL[tipo] : null;
  // EXP-TITULO: titulo obligatorio (min 3 chars).
  const valido = titulo.trim().length >= 3 && !!tipo && !!necesidad && descripcion.trim().length > 0;

  const crear = async () => {
    if (!valido || enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/oportunidades/${expedientePadre.id}/crear-hijo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          titulo: titulo.trim(),
          tipo,
          necesidad,
          descripcion: descripcion.trim(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 401 || r.status === 403) {
        setError('No tienes permisos.');
        return;
      }
      if (!r.ok || !data?.ok) throw new Error(data?.error || `Error ${r.status}`);
      setToast({ tipo: 'ok', msg: `Hijo ${data.oportunidad.folio} creado.` });
      setTimeout(() => onCreado(data.oportunidad), 900);
    } catch (e) {
      setError(e.message || 'No se pudo crear el expediente hijo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <>
      <ShellModal onClose={enviando ? () => {} : onCerrar} ancho={560} esCelular={esCelular}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${COLOR_BORDE}`,
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: COLOR_OSCURO, fontFamily: FF }}>
              Crear expediente vinculado
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: COLOR_GRIS_CL }}>
              Padre: <strong>{expedientePadre.folio}</strong>
              {expedientePadre.cliente_jerarquia_corta ? ` — ${expedientePadre.cliente_jerarquia_corta}` : ''}
            </div>
          </div>
          <button
            onClick={onCerrar}
            disabled={enviando}
            aria-label="Cerrar"
            style={{
              background: 'transparent', border: 'none',
              cursor: enviando ? 'not-allowed' : 'pointer',
              color: COLOR_GRIS_CL, fontSize: 22, padding: 4,
            }}
          >{'×'}</button>
        </div>

        <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' }}>
          {/* EXP-TITULO: cada anexo tiene su propio titulo (no se hereda) */}
          <Campo etiqueta="Titulo del expediente" requerido>
            <input
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value.slice(0, 150))}
              placeholder="Ej: Garantia camara fallada bahia 3"
              maxLength={150}
              disabled={enviando}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: COLOR_GRIS_CL, marginTop: 4 }}>
              {titulo.length}/150 caracteres
            </div>
          </Campo>

          <Campo etiqueta="Tipo" requerido>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr 1fr' }}>
              {TIPOS_HIJO.map(t => {
                const sel = tipo === t.v;
                const visual = TIPO_EXPEDIENTE_VISUAL[t.v];
                return (
                  <button
                    key={t.v}
                    type="button"
                    onClick={() => setTipo(t.v)}
                    title={t.descripcion}
                    style={{
                      background: sel ? visual.color + '14' : '#fff',
                      border: `2px solid ${sel ? visual.color : COLOR_BORDE}`,
                      borderRadius: 10, padding: '12px 10px',
                      cursor: 'pointer', fontFamily: FF,
                      color: sel ? visual.color : COLOR_OSCURO,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                      textAlign: 'center', minHeight: 80,
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{visual.icono}</span>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{t.label}</span>
                    <span style={{ fontSize: 10, color: COLOR_GRIS_CL, lineHeight: 1.3 }}>{t.descripcion}</span>
                  </button>
                );
              })}
            </div>
            {tipoMeta && (
              <div style={{
                marginTop: 8, fontSize: 11, color: COLOR_GRIS,
                background: tipoMeta.color + '14', padding: '6px 10px', borderRadius: 6,
              }}>
                Responsable se asignara segun tipo:
                {tipo === 'anexo'              && ' mismo vendedor del padre.'}
                {tipo === 'garantia'           && ' supervisor (Isaias), o vendedor del padre si no existe.'}
                {tipo === 'servicio_postventa' && ' comercial (Axel), o vendedor del padre si no existe.'}
              </div>
            )}
          </Campo>

          <Campo etiqueta="Necesidad" requerido>
            <select
              value={necesidad}
              onChange={(e) => setNecesidad(e.target.value)}
              disabled={enviando}
              style={inputStyle}
            >
              <option value="">-- selecciona una necesidad --</option>
              {necesidades.map(n => (
                <option key={n.codigo} value={n.codigo}>{n.icono} {n.nombre}</option>
              ))}
            </select>
          </Campo>

          <Campo etiqueta="Descripcion" requerido>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Detalla que se necesita en este expediente vinculado..."
              rows={3}
              disabled={enviando}
              style={{ ...inputStyle, resize: 'vertical', minHeight: 80, fontFamily: FF }}
            />
          </Campo>

          {error && (
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c',
              padding: '8px 12px', borderRadius: 8, fontSize: 12,
            }}>{error}</div>
          )}
        </div>

        <div style={{
          padding: 16, borderTop: `1px solid ${COLOR_BORDE}`, background: '#fafafa',
          display: 'flex', justifyContent: 'space-between', gap: 10,
        }}>
          <button onClick={onCerrar} disabled={enviando} style={botonSecundario}>Cancelar</button>
          <button
            onClick={crear}
            disabled={!valido || enviando}
            style={{
              ...botonPrimario,
              opacity: (!valido || enviando) ? 0.5 : 1,
              cursor: (!valido || enviando) ? 'not-allowed' : 'pointer',
            }}
          >{enviando ? 'Creando...' : 'Crear expediente vinculado'}</button>
        </div>
      </ShellModal>
      {toast && <ToastFlotante tipo={toast.tipo}>{toast.msg}</ToastFlotante>}
    </>
  );
}

function Campo({ etiqueta, requerido, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 12, color: COLOR_GRIS, fontWeight: 600, fontFamily: FF }}>
        {etiqueta}{requerido && <span style={{ color: COLOR_PELIGRO, marginLeft: 3 }}>*</span>}
      </span>
      {children}
    </label>
  );
}
